import React, { useState, useEffect } from 'react';
import { Grid, Stack, Divider, CircularProgress, Alert } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  Operation,
  getOperation,
  updateOperation,
  CreateOperationData,
  fetchOperationRoles,
  OperationRole
} from '../../../networkRequest/operationsService';
import { useUser } from '../../../hooks/useUser';
import { useOperationActions } from '../../../hooks/useOperationActions';
import { combineDateAndTime } from '../../../utils/dateUtils';
import { PageNavigation } from '@components';
import OperationDetails from './components/OperationDetails/OperationDetails';
import OperationLocations from './components/OperationLocations';
import EditButton from '../components/EditButton';
import { ExtraActionsMenu, ArchiveConfirmation, CloseOperationModal } from '../components';
import EditOperationModal from '../components/EditOperationModal';
import EditLocationModal from '../components/EditLocationModal';
import AddAdditionalLocationModal from '../components/AddAdditionalLocationModal';
import RemoveLocationModal from '../components/RemoveLocationModal';
import CommandPersonnelSection from './components/CommandPersonnelSection';
import EditIncidentCommandersModal from './components/EditIncidentCommandersModal';
import EditCommandStaffModal from './components/EditCommandStaffModal';
import ParticipantsSection from './components/Participants/ParticipantsSection';
import AddTeamModal from '../components/AddTeamModal';
import AddParticipantDialog from './components/Participants/AddParticipantDialog';
import { SearchableAgencyOfficialDropdownOption } from '@components/SearchableAgencyOfficialDropdown';
import { COPY, PAGE_PATHS } from '../../../constants';

export interface AddParticipantFormData {
  team_ids?: string[];
  assignment?: string;
}

const ViewOperationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [operation, setOperation] = useState<Operation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    agencyAssignedIdentifier: '',
    description: '',
    date: '',
    time: '',
    operationType: {
      planned: false,
      emergency: false
    }
  });
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [editLocationModalOpen, setEditLocationModalOpen] = useState(false);
  const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
  const [removeLocationModalOpen, setRemoveLocationModalOpen] = useState(false);
  const [locationToRemove, setLocationToRemove] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [editIncidentCommandersModalOpen, setEditIncidentCommandersModalOpen] = useState(false);
  const [editCommandStaffModalOpen, setEditCommandStaffModalOpen] = useState(false);
  const [operationRoles, setOperationRoles] = useState<OperationRole[]>([]);
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] = useState(false);
  const [selectedAgencyOfficial, setSelectedAgencyOfficial] = useState<SearchableAgencyOfficialDropdownOption | null>(null);

  const [addTeamModalOpen, setAddTeamModalOpen] = useState<boolean>(false);

  const {
    confirmArchiveOpen,
    operationToArchive,
    handleDuplicate,
    handleArchiveClick,
    handleArchiveConfirm,
    handleArchiveCancel
  } = useOperationActions({
    onError: (error) => setSaveError(error),
    onOperationChange: (updatedOperation) => setOperation(updatedOperation),
    allOperations: operation ? [operation] : []
  });

  const getOperationRoles = async (user) => {
    const { data, error } = (await fetchOperationRoles(user.accessToken)) || {};
    if (data) {
      setOperationRoles(data);
    }
    if (error) {
      console.error(error);
    }
  };


  const incidentCommander = operation?.operation_agency_officials?.find((operation_agency_official) => {
    return (operation_agency_official.operation_role.name === 'Incident Commander');
  });

  const deputyIncidentCommanders = operation?.operation_agency_officials?.filter((operation_agency_official) => {
    return operation_agency_official.operation_role.name === 'Deputy Incident Commander'
  })

  const commandStaffs = operation?.operation_agency_officials?.filter((operation_agency_official) => {
    return operation_agency_official.operation_role.name === 'Command Staff'
  })

  useEffect(() => {
    if (!user?.accessToken) return;
    getOperationRoles(user);
  }, [user?.accessToken]);

  useEffect(() => {
    const fetchOperation = async () => {
      if (!id || !user?.accessToken) {
        setError('Invalid operation ID or missing authentication');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getOperation(user.accessToken, id);

        if (response.error) {
          setError(response.error);
        } else if (response.data) {
          setOperation(response.data);
        } else {
          setError('Operation not found');
        }
      } catch {
        setError('Failed to load operation details');
      } finally {
        setLoading(false);
      }
    };

    fetchOperation();
  }, [id, user?.accessToken]);

  const handleEditClick = () => {
    if (operation) {
      // Transform operation data to match EditOperation form structure
      const startDate = new Date(operation.start_date);
      setFormData({
        title: operation.title,
        agencyAssignedIdentifier: operation.agency_assigned_identifier,
        description: operation.description,
        date: operation.start_date.split('T')[0], // Extract date part
        time: startDate.toTimeString().slice(0, 5), // Extract time part (HH:MM)
        operationType: {
          planned: operation.is_planned,
          emergency: operation.is_emergency
        }
      });
      setEditModalOpen(true);
    }
  };

  const handleSaveOperation = async (updatedFormData: {
    title: string;
    agencyAssignedIdentifier: string;
    description: string;
    date: string;
    time: string;
    operationType: {
      planned: boolean;
      emergency: boolean;
    };
  }) => {
    if (!user?.accessToken || !operation?.id) {
      setSaveError('Authentication required or operation not found');
      return;
    }

    setSaveLoading(true);
    setSaveError(null);

    try {
      // Transform form data to match API format
      const updateData: Partial<CreateOperationData> = {
        title: updatedFormData.title,
        agency_assigned_identifier: updatedFormData.agencyAssignedIdentifier,
        description: updatedFormData.description,
        is_planned: updatedFormData.operationType.planned,
        is_emergency: updatedFormData.operationType.emergency,
        start_date: combineDateAndTime(updatedFormData.date, updatedFormData.time)
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setSaveError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        setOperation(result.data);
      }

      // Close modal on successful save
      setEditModalOpen(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to update operation');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCloseClick = () => {
    if (operation) {
      // Set default end date/time to current date/time in user's local timezone
      const now = new Date();
      // Use local date methods to avoid UTC conversion issues
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      setEndDate(`${year}-${month}-${day}`); // YYYY-MM-DD format in local timezone
      setEndTime(now.toTimeString().slice(0, 5)); // HH:MM format (already local)
      setCloseModalOpen(true);
    }
  };

  const handleCloseOperation = async (closeEndDate: string, closeEndTime: string) => {
    if (!user?.accessToken || !operation?.id) {
      setSaveError('Authentication required or operation not found');
      return;
    }

    setCloseLoading(true);
    setSaveError(null);

    try {
      // Combine end date and time into ISO string format, preserving user's timezone
      const endDateTime = closeEndDate ? combineDateAndTime(closeEndDate, closeEndTime) : undefined;

      // Update operation with closed status and end date
      const updateData: Partial<CreateOperationData> = {
        status: 'closed',
        end_date: endDateTime
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setSaveError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        setOperation(result.data);
      }

      // Close modal on successful save
      setCloseModalOpen(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to close operation');
    } finally {
      setCloseLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: Operation['status']) => {
    if (!user?.accessToken || !operation?.id) {
      setSaveError('Authentication required or operation not found');
      return;
    }

    // If user is trying to archive, show confirmation dialog instead
    if (newStatus === 'archived') {
      handleArchiveClick(operation);
      return;
    }

    // If user is trying to close, show close operation modal instead
    if (newStatus === 'closed') {
      handleCloseClick();
      return;
    }

    try {
      const updateData: Partial<CreateOperationData> = {
        status: newStatus
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setSaveError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        setOperation(result.data);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to update operation status');
    }
  };

  const handleAddTeam = async (name: string) => {
    setSaveLoading(true);
    setSaveError(null);
    try {
      if (!user?.accessToken || !operation?.id) {
        setSaveError('Authentication required or operation not found');
        return;
      }

      const updateData: Partial<CreateOperationData> = {
        operation_teams_attributes: [{ name: name }]
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setSaveError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        setOperation(result.data);
      }

      // Close modal on successful save
      setAddTeamModalOpen(false);
    }
    catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to add team');
    }
    finally {
      setSaveLoading(false);
    }
  };

  const handleRemoveLocationClick = (locationId: string) => {
    setLocationToRemove(locationId);
    setRemoveLocationModalOpen(true);
  };

  const handleRemoveLocationModalClose = () => {
    setRemoveLocationModalOpen(false);
    setLocationToRemove(null);
  };

  const handleRemoveLocation = async () => {
    if (!user?.accessToken || !operation?.id || !locationToRemove) {
      setSaveError('Authentication required or location not found');
      return;
    }

    setRemoveLoading(true);
    setSaveError(null);

    try {
      // Use _destroy flag to mark the location for deletion
      const updateData = {
        operation_locations_attributes: [
          {
            id: locationToRemove,
            _destroy: true
          }
        ]
      } as any;

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setSaveError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        setOperation(result.data);
      }

      // Close modal on successful removal
      handleRemoveLocationModalClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to remove location');
    } finally {
      setRemoveLoading(false);
    }
  };

  const resetAddParticipant = () => {
    setAddParticipantDialogOpen(false);
    setSelectedAgencyOfficial(null);
  };

  if (loading) {
    return (
      <Grid
        container
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!operation) {
    return <Alert severity="warning">Operation not found</Alert>;
  }

  return (
    <>
      <Stack spacing={4}>
        {saveError && (
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        <PageNavigation
          breadcrumbs={[{ display: COPY.titles.operations, path: PAGE_PATHS.operations }]}
          controls={[
            <EditButton key="edit" onClick={handleEditClick} text="Operation Details" />,
            <ExtraActionsMenu
              onDuplicate={() => operation && handleDuplicate(operation)}
              onArchive={() => operation && handleArchiveClick(operation)}
              archiveDisabled={operation?.status === 'archived'}
            />
          ]}
        />
        <OperationDetails operation={operation} onStatusChange={handleStatusChange} />

        <Divider />
        <OperationLocations
          operation={operation}
          onEditPrimaryLocation={() => setEditLocationModalOpen(true)}
          onAddLocation={() => setAddLocationModalOpen(true)}
          onRemoveLocation={handleRemoveLocationClick}
        />

        <Divider />
        <CommandPersonnelSection
          operation={operation}
          onEditIncidentCommanders={() => setEditIncidentCommandersModalOpen(true)}
          onEditCommandStaff={() => setEditCommandStaffModalOpen(true)}
        />

        <Divider />
        <ParticipantsSection
          operation={operation}
          onOperationUpdate={setOperation}
          onAddTeam={() => { setAddTeamModalOpen(true); }}
          setAddParticipantDialogOpen={setAddParticipantDialogOpen}
          setSelectedAgencyOfficial={setSelectedAgencyOfficial}
          selectedAgencyOfficial={selectedAgencyOfficial}
        />
      </Stack>

      <EditOperationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveOperation}
        formData={formData}
        onFormDataChange={setFormData}
        onValidationError={(errors) => {
          setSaveError(`Please fix the following errors: ${errors.join(', ')}`);
        }}
        loading={saveLoading}
      />

      <EditLocationModal
        open={editLocationModalOpen}
        onClose={() => setEditLocationModalOpen(false)}
        primaryLocation={operation.operation_locations.find((loc) => loc.primary) ?? {
          id: '',
          name: '',
          street_address_1: '',
          street_address_2: '',
          city: '',
          state: '',
          zip: '',
          primary: true,
        }}
        operation={operation}
        onOperationUpdate={setOperation}
      />

      <AddAdditionalLocationModal
        open={addLocationModalOpen}
        onClose={() => setAddLocationModalOpen(false)}
        operation={operation}
        onOperationUpdate={setOperation}
      />

      <RemoveLocationModal
        open={removeLocationModalOpen}
        onClose={handleRemoveLocationModalClose}
        onRemoveLocation={handleRemoveLocation}
        loading={removeLoading}
      />

      <CloseOperationModal
        open={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        onCloseOperation={handleCloseOperation}
        endDate={endDate}
        endTime={endTime}
        onEndDateChange={setEndDate}
        onEndTimeChange={setEndTime}
        loading={closeLoading}
      />

      <ArchiveConfirmation
        open={confirmArchiveOpen}
        operationTitle={operationToArchive}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />

      <EditIncidentCommandersModal
        open={editIncidentCommandersModalOpen}
        onClose={() => setEditIncidentCommandersModalOpen(false)}
        operation={operation}
        incidentCommander={incidentCommander}
        deputyIncidentCommanders={deputyIncidentCommanders ?? []}
        onOperationUpdate={(updatedOp) => setOperation(updatedOp)}
        operationRoles={operationRoles}
      />

      <EditCommandStaffModal
        open={editCommandStaffModalOpen}
        onClose={() => setEditCommandStaffModalOpen(false)}
        operation={operation}
        commandStaffs={commandStaffs ?? []}
        onOperationUpdate={(updatedOp) => setOperation(updatedOp)}
        operationRoles={operationRoles}
      />

      <AddTeamModal
        open={addTeamModalOpen}
        onClose={() => { setAddTeamModalOpen(false); }}
        onSave={handleAddTeam}
        operationTeams={operation.operation_teams}
        onValidationError={(errors) => { setSaveError(`Please fix the following errors: ${errors.join(', ')}`); }}
        loading={saveLoading}
      />

      <AddParticipantDialog
        open={addParticipantDialogOpen}
        onClose={() => resetAddParticipant()}
        onOperationUpdate={(updatedOp) => setOperation(updatedOp)}
        operation={operation}
        operationRoles={operationRoles}
        selectedAgencyOfficial={selectedAgencyOfficial}
        teams={operation.operation_teams}
      />
    </>
  );
};

export default ViewOperationPage;
